<template>
  <div class="landing">
    <!-- Dateline / broadsheet header -->
    <div class="landing__dateline">
      <div class="landing__dateline-inner">
        <span class="dateline__item">Vol. {{ buildVolume }} &middot; N° {{ buildIssue }}</span>
        <span class="dateline__item dateline__item--center">
          {{ formattedDate }}
          <span class="dateline__dot">♦</span>
          Pressbox Edition
        </span>
        <span class="dateline__item dateline__item--right"
          >Established 1871 &mdash; Baseball's First Season</span
        >
      </div>
    </div>

    <!-- ════════════════════════════════════════════════════════════════
         HERO — Broadsheet front page
         ════════════════════════════════════════════════════════════════ -->
    <section class="hero" aria-label="Front page">
      <div class="hero__inner">
        <div class="hero__left">
          <p class="eyebrow hero__kicker">The Pitch &mdash; A Special Report</p>
          <h1 class="hero__headline">
            The hidden <em>edge</em> in<br />
            two centuries of<br />
            <span class="hero__headline-accent">baseball numbers.</span>
          </h1>
          <div class="hero__byline">
            <span class="byline__rule"></span>
            <span class="byline__text">By the HomerunData Newsroom &middot; Analytics Desk</span>
            <span class="byline__rule"></span>
          </div>
          <p class="hero__lede">
            <span class="drop-cap">T</span>raditional statistics have looked away from inconvenient
            truths for 150&nbsp;years. We built metrics, simulations, and a betting intelligence
            engine that don't. Every plate appearance. Every bullpen decision. Every umpire's quiet
            thumb on the scale. Measured, modeled, surfaced.
          </p>
          <div class="hero__cta-row">
            <button class="btn-press" @click="handleExplore">
              Read the Analysis
              <span class="btn-press__arrow">&rarr;</span>
            </button>
            <router-link to="/login" class="btn-press btn-press--ghost">
              Subscribe to Access
            </router-link>
          </div>
        </div>

        <aside class="hero__right" aria-label="By the Numbers">
          <div class="boxscore">
            <div class="boxscore__header">
              <span class="boxscore__title">By the Numbers</span>
              <span class="boxscore__edition">Tonight's Line</span>
            </div>
            <ol class="boxscore__rows">
              <li v-for="(stat, i) in heroStats" :key="stat.label" class="boxscore__row">
                <span class="boxscore__n">{{ String(i + 1).padStart(2, '0') }}</span>
                <span class="boxscore__label">{{ stat.label }}</span>
                <span class="boxscore__dots" aria-hidden="true"></span>
                <span class="boxscore__value numeric">{{ stat.value }}</span>
              </li>
            </ol>
            <div class="boxscore__footer">
              <span>♦</span>
              <span class="boxscore__signature">Box &middot; HomerunData</span>
              <span>♦</span>
            </div>
          </div>
        </aside>
      </div>
    </section>

    <!-- ════════════════════════════════════════════════════════════════
         I. METRICS
         ════════════════════════════════════════════════════════════════ -->
    <section id="metrics" class="section section--metrics">
      <div class="section__header">
        <div class="section-number">I</div>
        <div class="section__titles">
          <p class="eyebrow">Proprietary Metrics &mdash; Eight New Statistics</p>
          <h2 class="display-lg">Traditional stats have blind spots. <em>Ours don't.</em></h2>
          <p class="body-lg section__dek">
            Every metric is purpose-built to correct a specific flaw in conventional baseball
            statistics. Named, branded, and ready for the leaderboard.
          </p>
        </div>
      </div>
      <hr class="rule rule--double" />
      <div class="metrics-grid">
        <article
          v-for="(metric, index) in metrics"
          :key="metric.name"
          class="metric"
          :style="{ '--delay': `${index * 0.06}s` }"
        >
          <div class="metric__top">
            <span class="metric__category">{{ metric.category }}</span>
            <span class="metric__n numeric">№ {{ String(index + 1).padStart(2, '0') }}</span>
          </div>
          <h3 class="metric__name">{{ metric.name }}</h3>
          <p class="metric__hook">{{ metric.hook }}</p>
        </article>
      </div>
    </section>

    <hr class="rule rule--ox" />

    <!-- ════════════════════════════════════════════════════════════════
         II. SIMULATION
         ════════════════════════════════════════════════════════════════ -->
    <section id="simulation" class="section section--sim">
      <div class="section__header">
        <div class="section-number">II</div>
        <div class="section__titles">
          <p class="eyebrow">Game Simulation Engine</p>
          <h2 class="display-lg">Thousands of games played<br />before the first pitch.</h2>
          <p class="body-lg section__dek">
            Every matchup runs through a full simulation engine that models each plate appearance,
            bullpen transition, and lineup substitution. The result isn't a single number &mdash;
            it's a complete probability distribution.
          </p>
        </div>
      </div>
      <hr class="rule" />
      <div class="sim-grid">
        <article v-for="(output, i) in simOutputs" :key="output.title" class="sim-card">
          <span class="sim-card__corner" aria-hidden="true">{{
            String(i + 1).padStart(2, '0')
          }}</span>
          <h3 class="sim-card__title">{{ output.title }}</h3>
          <p class="sim-card__desc">{{ output.description }}</p>
          <router-link
            v-if="output.title === 'Simulated Box Scores'"
            to="/boxscore-demo"
            class="sim-card__link link-ink"
          >
            See an example
            <span>&rarr;</span>
          </router-link>
        </article>
      </div>
    </section>

    <hr class="rule rule--ox" />

    <!-- ════════════════════════════════════════════════════════════════
         III. SITUATIONAL INTELLIGENCE
         ════════════════════════════════════════════════════════════════ -->
    <section id="intelligence" class="section section--sit">
      <div class="section__header">
        <div class="section-number">III</div>
        <div class="section__titles">
          <p class="eyebrow">Situational Intelligence</p>
          <h2 class="display-lg">Context changes <em>everything.</em></h2>
          <p class="body-lg section__dek">
            Projections adjust in real time based on who's behind the plate, what the weather is
            doing, how tired the bullpen is, and how the manager deploys his roster.
          </p>
        </div>
      </div>
      <hr class="rule" />
      <div class="sit-grid">
        <div v-for="factor in situationalFactors" :key="factor.title" class="sit-item">
          <div class="sit-item__hash" aria-hidden="true">§</div>
          <h3 class="sit-item__title">{{ factor.title }}</h3>
          <p class="sit-item__detail">{{ factor.detail }}</p>
        </div>
      </div>
    </section>

    <hr class="rule rule--ox" />

    <!-- ════════════════════════════════════════════════════════════════
         IV. THE DUGOUT
         ════════════════════════════════════════════════════════════════ -->
    <section id="dugout" class="section section--dugout">
      <div class="section__header">
        <div class="section-number">IV</div>
        <div class="section__titles">
          <p class="eyebrow">The Dugout &mdash; An Editorial Note</p>
          <h2 class="display-lg">Where strategy meets <em>obsession.</em></h2>
        </div>
      </div>
      <hr class="rule" />
      <div class="dugout-grid">
        <article v-for="(card, index) in dugoutCards" :key="card.title" class="dugout-card">
          <div class="dugout-card__top">
            <span class="dugout-card__number">{{ String(index + 1).padStart(2, '0') }}</span>
            <span class="dugout-card__tag">Est. 1871</span>
          </div>
          <h3 class="dugout-card__title">{{ card.title }}</h3>
          <p class="dugout-card__desc">{{ card.description }}</p>
        </article>
      </div>
    </section>

    <!-- ════════════════════════════════════════════════════════════════
         COLOPHON / FOOTER
         ════════════════════════════════════════════════════════════════ -->
    <footer class="colophon">
      <div class="colophon__rule" aria-hidden="true"></div>
      <div class="colophon__inner">
        <div class="colophon__left">
          <span class="colophon__brand">HomerunData</span>
          <span class="colophon__tag">Pressbox Edition</span>
        </div>
        <div class="colophon__center">
          <p>
            Set in <em>Fraunces</em>, <em>Newsreader</em>, <em>Syne</em>, and
            <em>JetBrains Mono</em>.<br />
            Typeset from first pitch to final out.
          </p>
        </div>
        <div class="colophon__right">
          &copy; {{ currentYear }} &middot; Build {{ buildVolume }}.{{ buildIssue }}
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface DugoutCard {
  title: string
  description: string
}
interface Metric {
  name: string
  category: string
  hook: string
}
interface SimOutput {
  title: string
  description: string
}
interface SituationalFactor {
  title: string
  detail: string
}

const metrics: Metric[] = [
  {
    name: 'TWHIP',
    category: 'Pitching',
    hook: 'WHIP ignores hit batters. Fifteen HBPs is fifteen phantom baserunners. TWHIP counts them.'
  },
  {
    name: 'TERA',
    category: 'Pitching',
    hook: "When a pitcher boots a comebacker, ERA calls it unearned. The pitcher caused it. TERA doesn't look away."
  },
  {
    name: 'SIERA+',
    category: 'Pitching',
    hook: 'SIERA measures pitcher quality from K%, BB%, and GB%. It ignores HBP rate entirely. SIERA+ fixes that.'
  },
  {
    name: 'TOBP',
    category: 'Batting',
    hook: "Reached on error? OBP pretends it didn't happen. TOBP gives credit where the batter earned it."
  },
  {
    name: 'BIPPI',
    category: 'Batting',
    hook: 'Not all errors are gifts. Fast, pull-heavy hitters force harder plays. BIPPI quantifies defensive pressure.'
  },
  {
    name: 'TSLG',
    category: 'Batting',
    hook: 'A double at Coors in July is not a double at Oracle in April. TSLG adjusts for context.'
  },
  {
    name: 'PFI',
    category: 'Fielding',
    hook: "How much does a pitcher's own glove help or hurt his run prevention? PFI isolates the answer."
  },
  {
    name: 'TOPS',
    category: 'Batting',
    hook: 'TOBP + TSLG. Corrected on-base plus park-normalized power. The version of OPS that should have existed all along.'
  }
]

const simOutputs: SimOutput[] = [
  {
    title: 'Simulated Box Scores',
    description:
      "See what tonight's game looks like before it happens. Projected line scores, key matchup previews, and probable outcomes."
  },
  {
    title: 'Player Prop Projections',
    description:
      'Strikeout totals, hit counts, home runs, total bases. Full probability distributions, not just a single over/under.'
  },
  {
    title: 'Win Probability',
    description:
      'Not a coin flip. A complete distribution built from thousands of simulated innings with real bullpen and lineup dynamics.'
  },
  {
    title: 'Edge Detection',
    description:
      "Compare model probabilities to sportsbook lines. When there's a gap, you'll know it \u2014 and how much to trust it."
  }
]

const situationalFactors: SituationalFactor[] = [
  {
    title: 'Umpire Tendencies',
    detail:
      'Each home plate umpire shifts the strike zone differently. We track K-rate boost, walk tendency, and zone consistency per umpire.'
  },
  {
    title: 'Bullpen Fatigue',
    detail:
      'Which relievers are available tonight? Pitch counts, days rest, and recent workload combine into fatigue state projections.'
  },
  {
    title: 'Weather & Park',
    detail:
      "A game-specific environmental model, not a season-long average. Temperature, wind, altitude \u2014 and how each batter's profile interacts with it."
  },
  {
    title: 'Matchup Depth',
    detail:
      'Platoon splits, bench usage predictions, and manager tendencies. We model the substitutions most projections ignore.'
  },
  {
    title: 'Catcher Framing',
    detail:
      'The best framers save 20+ runs a season. We adjust pitcher projections based on who is behind the plate.'
  },
  {
    title: 'Baserunning',
    detail:
      'First-to-third ability, scoring from second, tag-up rates. Separated into components, not a single lumped number.'
  }
]

const dugoutCards: DugoutCard[] = [
  {
    title: 'Baseball in the Blood',
    description:
      "The kind of fan whose mom confiscated baseball cards as punishment. This isn't a data exercise. It's an obsession with a keyboard."
  },
  {
    title: 'Neural Nets Before They Were Cool',
    description:
      'Training models before \u201CAI\u201D was a LinkedIn buzzword. Came back for the modern ML wave with fresh eyes and old instincts.'
  },
  {
    title: 'Two Decades in the Code',
    description:
      "25+ years building software systems. The engineering behind HomerunData isn't a weekend experiment \u2014 it's backed by a career spent solving hard problems."
  },
  {
    title: 'Built to Find Edges',
    description:
      'Where engineering discipline meets baseball obsession. Every metric, model, and simulation exists because the market has a blind spot.'
  }
]

const heroStats = [
  { value: '219K+', label: 'Games on the Record' },
  { value: '15M+', label: 'Plays Dissected' },
  { value: '153', label: 'Seasons Indexed' },
  { value: '30', label: 'Franchises Modeled' }
]

const now = new Date()
const currentYear = computed((): number => now.getFullYear())
const formattedDate = computed((): string =>
  now
    .toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
    .toUpperCase()
)

// Build metadata injected at container build time.
// VITE_COMMIT_SHA — full git SHA; we show the first 7 chars as the "volume".
// VITE_BUILD_NUMBER — Gitea Actions run number; shown as the "issue" number.
// Both fall back to "dev" / day-of-year when building locally.
const commitSha = (import.meta.env.VITE_COMMIT_SHA as string | undefined) ?? ''
const buildNumber = (import.meta.env.VITE_BUILD_NUMBER as string | undefined) ?? ''

const buildVolume = computed((): string => (commitSha ? commitSha.slice(0, 7) : 'dev'))
const buildIssue = computed((): string => {
  if (buildNumber) return buildNumber
  const dayOfYear = Math.floor(
    (now.getTime() - new Date(now.getFullYear(), 0, 0).getTime()) / 86400000
  )
  return String(dayOfYear)
})

function handleExplore(): void {
  const el = document.getElementById('metrics')
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
}
</script>

<style scoped>
.landing {
  padding-top: 4.75rem; /* below masthead */
  color: var(--ink);
}

/* ─── DATELINE ─────────────────────────────────────────────────────── */
.landing__dateline {
  border-top: var(--rule-thin) solid var(--ink);
  border-bottom: var(--rule-thin) solid var(--ink);
  background: var(--paper);
}

.landing__dateline-inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0.55rem 2rem;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 1.5rem;
  font-family: var(--font-ui);
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  color: var(--ink-medium);
}

.dateline__item--center {
  text-align: center;
}

.dateline__item--right {
  text-align: right;
}

.dateline__dot {
  display: inline-block;
  margin: 0 0.6em;
  color: var(--ox);
  transform: translateY(-1px);
}

/* ─── HERO ─────────────────────────────────────────────────────────── */
.hero {
  max-width: 1280px;
  margin: 0 auto;
  padding: 4rem 2rem 5rem;
  position: relative;
}

.hero::before {
  /* giant ghost baseball in the corner */
  content: '';
  position: absolute;
  top: -2rem;
  right: -6rem;
  width: 460px;
  height: 460px;
  border: var(--rule-thin) solid var(--ink-20);
  border-radius: 50%;
  background: radial-gradient(
    circle at 30% 30%,
    transparent 0%,
    transparent 49%,
    var(--ink-10) 50%,
    transparent 52%
  );
  pointer-events: none;
  z-index: 0;
}

.hero__inner {
  display: grid;
  grid-template-columns: 1.7fr 1fr;
  gap: 4rem;
  align-items: start;
  position: relative;
  z-index: 1;
}

.hero__kicker {
  margin-bottom: 1.5rem;
}

.hero__headline {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 144,
    'SOFT' 30,
    'wght' 700;
  font-size: clamp(3rem, 7.5vw, 6rem);
  line-height: 0.92;
  letter-spacing: -0.03em;
  color: var(--ink);
  margin-bottom: 2rem;
  max-width: 14ch;
}

.hero__headline em {
  font-style: italic;
  color: var(--ox);
  font-variation-settings:
    'opsz' 144,
    'SOFT' 100,
    'wght' 500;
}

.hero__headline-accent {
  font-style: italic;
  color: var(--ink);
  font-variation-settings:
    'opsz' 144,
    'SOFT' 80,
    'wght' 500;
  border-bottom: 4px solid var(--ox);
  padding-bottom: 0.05em;
}

.hero__byline {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
}

.byline__rule {
  flex: 1;
  height: 1px;
  background: var(--ink);
  max-width: 60px;
}

.byline__text {
  font-family: var(--font-ui);
  font-size: 0.72rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: var(--ink-medium);
}

.hero__lede {
  font-family: var(--font-editorial);
  font-size: 1.25rem;
  line-height: 1.55;
  color: var(--ink-medium);
  max-width: 42ch;
  margin-bottom: 2.5rem;
}

.drop-cap {
  float: left;
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 144,
    'wght' 800;
  font-size: 5.5rem;
  line-height: 0.82;
  padding: 0.3rem 0.55rem 0 0;
  color: var(--ox);
}

.hero__cta-row {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.btn-press__arrow {
  display: inline-block;
  transition: transform 0.18s ease;
}

.btn-press:hover .btn-press__arrow {
  transform: translateX(4px);
}

/* ─── Boxscore (hero right rail) ───────────────────────────────────── */
.boxscore {
  background: var(--paper);
  border: var(--rule-thin) solid var(--ink);
  padding: 1.75rem;
  box-shadow: 6px 6px 0 var(--ink);
  position: relative;
}

.boxscore__header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding-bottom: 0.75rem;
  margin-bottom: 1rem;
  border-bottom: var(--rule-thin) solid var(--ink);
}

.boxscore__title {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 36,
    'wght' 700;
  font-size: 1.35rem;
  letter-spacing: -0.01em;
}

.boxscore__edition {
  font-family: var(--font-ui);
  font-size: 0.62rem;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  color: var(--ox);
}

.boxscore__rows {
  list-style: none;
  padding: 0;
  margin: 0 0 1rem 0;
}

.boxscore__row {
  display: grid;
  grid-template-columns: 2rem 1fr auto auto;
  gap: 0.5rem;
  align-items: baseline;
  padding: 0.55rem 0;
  border-bottom: var(--rule-thin) dashed var(--paper-edge);
}

.boxscore__row:last-child {
  border-bottom: none;
}

.boxscore__n {
  font-family: var(--font-data);
  font-size: 0.7rem;
  color: var(--ink-ghost);
}

.boxscore__label {
  font-family: var(--font-editorial);
  font-size: 0.95rem;
  color: var(--ink);
}

.boxscore__dots {
  height: 1em;
  border-bottom: 1px dotted var(--ink-ghost);
  flex: 1;
  margin: 0 0.3rem;
  transform: translateY(-0.18em);
}

.boxscore__value {
  font-weight: 700;
  font-size: 1.05rem;
  color: var(--ox);
}

.boxscore__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.5rem;
  border-top: var(--rule-thin) solid var(--ink);
  color: var(--ox);
  font-family: var(--font-ui);
  font-size: 0.62rem;
  text-transform: uppercase;
  letter-spacing: 0.24em;
}

.boxscore__signature {
  color: var(--ink-medium);
}

/* ─── Sections (shared) ───────────────────────────────────────────── */
.section {
  max-width: 1280px;
  margin: 0 auto;
  padding: 5rem 2rem;
}

.section__header {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2rem;
  align-items: start;
  max-width: 960px;
}

.section__titles .eyebrow {
  margin-bottom: 1rem;
}

.section__titles .display-lg em {
  font-style: italic;
  color: var(--ox);
}

.section__dek {
  margin-top: 1.25rem;
  max-width: 58ch;
}

/* ─── I. METRICS GRID ─────────────────────────────────────────────── */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  border: var(--rule-thin) solid var(--ink);
}

.metric {
  padding: 2rem 1.5rem 1.75rem;
  background: var(--paper);
  position: relative;
  transition: background 0.18s ease;
  animation: fade-up 0.65s ease-out both;
  animation-delay: var(--delay, 0s);
}

/* Vertical rules between columns (items NOT in col 1) */
.metric:not(:nth-child(4n + 1)) {
  border-left: var(--rule-thin) solid var(--ink);
}

/* Horizontal rule between rows (items in row 2+) */
.metric:nth-child(n + 5) {
  border-top: var(--rule-thin) solid var(--ink);
}

.metric:hover {
  background: var(--paper-deep);
}

.metric__top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 1.25rem;
}

.metric__category {
  font-family: var(--font-ui);
  font-size: 0.62rem;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  color: var(--ox);
}

.metric__n {
  font-size: 0.7rem;
  color: var(--ink-ghost);
}

.metric__name {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 72,
    'wght' 700;
  font-size: 2.25rem;
  letter-spacing: -0.02em;
  line-height: 1;
  color: var(--ink);
  margin-bottom: 0.85rem;
}

.metric__hook {
  font-family: var(--font-editorial);
  font-size: 0.95rem;
  line-height: 1.55;
  color: var(--ink-medium);
}

@keyframes fade-up {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ─── II. SIMULATION GRID ─────────────────────────────────────────── */
.sim-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
}

.sim-card {
  position: relative;
  padding: 2rem 2rem 2rem 4rem;
  background: var(--paper);
  border: var(--rule-thin) solid var(--ink);
  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease;
}

.sim-card:hover {
  transform: translate(-2px, -2px);
  box-shadow: 4px 4px 0 var(--ink);
}

.sim-card__corner {
  position: absolute;
  top: 1.25rem;
  left: 1.25rem;
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 72,
    'wght' 700;
  font-size: 1.5rem;
  font-style: italic;
  color: var(--ox);
}

.sim-card__title {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 36,
    'wght' 600;
  font-size: 1.6rem;
  line-height: 1.1;
  margin-bottom: 0.85rem;
  color: var(--ink);
}

.sim-card__desc {
  font-family: var(--font-editorial);
  font-size: 1rem;
  line-height: 1.55;
  color: var(--ink-medium);
  margin-bottom: 1rem;
}

.sim-card__link {
  display: inline-flex;
  gap: 0.4rem;
  font-family: var(--font-ui);
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.16em;
}

/* ─── III. SITUATIONAL ────────────────────────────────────────────── */
.sit-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
}

.sit-item {
  padding: 2rem 2rem 2rem 3.5rem;
  border-left: var(--rule-thin) solid var(--ink);
  position: relative;
}

/* First column in the 3-col grid (items 1, 4, 7...) — no left border. */
.sit-item:nth-child(3n + 1) {
  border-left: none;
}

.sit-item__hash {
  position: absolute;
  top: 1.5rem;
  left: 1.25rem;
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 72,
    'wght' 600;
  font-size: 1.6rem;
  color: var(--ox);
  font-style: italic;
}

.sit-item__title {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 36,
    'wght' 600;
  font-size: 1.3rem;
  letter-spacing: -0.01em;
  margin-bottom: 0.65rem;
  color: var(--ink);
}

.sit-item__detail {
  font-family: var(--font-editorial);
  font-size: 0.98rem;
  line-height: 1.55;
  color: var(--ink-medium);
}

/* ─── IV. DUGOUT ──────────────────────────────────────────────────── */
.dugout-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 3rem 4rem;
}

.dugout-card {
  padding-left: 2rem;
  border-left: var(--rule-thick) solid var(--ox);
}

.dugout-card__top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.85rem;
}

.dugout-card__number {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 144,
    'wght' 800;
  font-size: 3.5rem;
  line-height: 0.85;
  color: var(--ink);
  font-style: italic;
}

.dugout-card__tag {
  font-family: var(--font-ui);
  font-size: 0.62rem;
  text-transform: uppercase;
  letter-spacing: 0.24em;
  color: var(--ink-faint);
}

.dugout-card__title {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 48,
    'wght' 600;
  font-size: 1.75rem;
  line-height: 1.1;
  color: var(--ink);
  margin-bottom: 0.85rem;
}

.dugout-card__desc {
  font-family: var(--font-editorial);
  font-size: 1.05rem;
  line-height: 1.6;
  color: var(--ink-medium);
}

/* ─── COLOPHON / FOOTER ───────────────────────────────────────────── */
.colophon {
  margin-top: 2rem;
  background: var(--paper-deep);
}

.colophon__rule {
  height: 5px;
  border-top: 3px solid var(--ink);
  border-bottom: 1px solid var(--ink);
}

.colophon__inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 2.5rem 2rem;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 2rem;
  align-items: center;
  font-family: var(--font-ui);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: var(--ink-medium);
}

.colophon__left {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.colophon__brand {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 36,
    'wght' 700;
  font-size: 1.3rem;
  text-transform: none;
  letter-spacing: -0.01em;
  color: var(--ink);
}

.colophon__tag {
  color: var(--ox);
  font-size: 0.6rem;
  letter-spacing: 0.26em;
}

.colophon__center {
  text-align: center;
  font-family: var(--font-editorial);
  font-size: 0.85rem;
  text-transform: none;
  letter-spacing: 0;
  color: var(--ink-faint);
}

.colophon__center em {
  color: var(--ink);
  font-style: italic;
}

.colophon__right {
  text-align: right;
  color: var(--ink-faint);
}

/* ─── RESPONSIVE ─────────────────────────────────────────────────── */
@media (max-width: 1024px) {
  .hero__inner {
    grid-template-columns: 1fr;
    gap: 3rem;
  }

  .metrics-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  /* Reset desktop 4-col rules */
  .metric:not(:nth-child(4n + 1)) {
    border-left: none;
  }
  .metric:nth-child(n + 5) {
    border-top: none;
  }
  /* 2-col: second column gets left border; rows 2+ get top border */
  .metric:not(:nth-child(2n + 1)) {
    border-left: var(--rule-thin) solid var(--ink);
  }
  .metric:nth-child(n + 3) {
    border-top: var(--rule-thin) solid var(--ink);
  }

  .sit-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  /* Restore border on items that were first-col at 3-col but aren't at 2-col. */
  .sit-item:nth-child(3n + 1) {
    border-left: var(--rule-thin) solid var(--ink);
  }

  /* 2-col: first column is items 1, 3, 5. */
  .sit-item:nth-child(odd) {
    border-left: none;
  }

  .dugout-grid {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
}

@media (max-width: 700px) {
  .landing__dateline-inner {
    grid-template-columns: 1fr;
    gap: 0.35rem;
    text-align: center;
  }

  .dateline__item--right {
    text-align: center;
  }

  .section__header {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .metrics-grid {
    grid-template-columns: 1fr;
  }

  /* Reset rules and apply single top border between rows */
  .metric:not(:nth-child(4n + 1)),
  .metric:not(:nth-child(2n + 1)) {
    border-left: none;
  }
  .metric:nth-child(n + 2) {
    border-top: var(--rule-thin) solid var(--ink);
  }

  .sim-grid {
    grid-template-columns: 1fr;
  }

  .sit-grid {
    grid-template-columns: 1fr;
  }

  .sit-item {
    border-left: var(--rule-thick) solid var(--ox);
  }

  .colophon__inner {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .colophon__left,
  .colophon__right {
    text-align: center;
  }
}
</style>
